<template>
  <div class="client-detail">
    <div v-if="loading" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i>
      <p>Loading client details...</p>
    </div>
    
    <div v-else-if="!client" class="error-state">
      <i class="fas fa-exclamation-triangle"></i>
      <p>Client not found</p>
      <router-link to="/clients" class="btn-primary">Return to Clients</router-link>
    </div>

    <div v-else class="client-detail-content">
      <!-- Client Header -->
      <div class="client-header">
        <div class="header-content">
          <div class="client-info">
            <div class="breadcrumb">
              <router-link to="/clients">Clients</router-link>
              <span class="separator">›</span>
              <span class="current">{{ client.name }}</span>
            </div>
            <h1 class="client-name">{{ client.name }}</h1>
            <p class="client-type">{{ client.type }}</p>
            <div class="client-meta">
              <div class="meta-item">
                <i class="fas fa-calendar-alt"></i>
                <span>Client since {{ client.joinDate }}</span>
              </div>
              <div class="meta-item">
                <i class="fas fa-user"></i>
                <span>Primary Contact: {{ client.primaryContact }}</span>
              </div>
              <div class="meta-item">
                <i class="fas fa-envelope"></i>
                <span>{{ client.email }}</span>
              </div>
            </div>
          </div>
          <div class="client-status-card">
            <div class="status-indicator" :class="client.status">
              <span class="status-dot"></span>
              {{ client.status.charAt(0).toUpperCase() + client.status.slice(1) }}
            </div>
            <div class="compliance-score">
              <div class="score-circle" :class="getScoreClass(client.complianceScore)">
                <span class="score-number">{{ client.complianceScore }}%</span>
                <span class="score-label">Compliance</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Client Metrics Overview -->
      <div class="metrics-section">
        <div class="metrics-grid">
          <div class="metric-card">
            <div class="metric-icon">
              <i class="fas fa-folder"></i>
            </div>
            <div class="metric-content">
              <h3>{{ client.projects.length }}</h3>
              <p>Total Projects</p>
            </div>
          </div>
          
          <div class="metric-card">
            <div class="metric-icon">
              <i class="fas fa-users"></i>
            </div>
            <div class="metric-content">
              <h3>{{ client.activeSubjects }}</h3>
              <p>Active Subjects</p>
            </div>
          </div>
          
          <div class="metric-card">
            <div class="metric-icon">
              <i class="fas fa-exclamation-triangle"></i>
            </div>
            <div class="metric-content">
              <h3>{{ client.pendingDecisions }}</h3>
              <p>Pending Decisions</p>
            </div>
          </div>
          
          <div class="metric-card">
            <div class="metric-icon">
              <i class="fas fa-chart-line"></i>
            </div>
            <div class="metric-content">
              <h3>{{ client.monthlyTrend }}%</h3>
              <p>Monthly Improvement</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Projects Portfolio -->
      <div class="projects-section">
        <div class="section-header">
          <h2>Project Portfolio</h2>
          <div class="section-actions">
            <button class="btn-secondary" @click="toggleProjectView">
              <i :class="projectViewMode === 'grid' ? 'fas fa-list' : 'fas fa-th'"></i>
              {{ projectViewMode === 'grid' ? 'List View' : 'Grid View' }}
            </button>
            <router-link :to="`/clients/${client.id}/workflow-settings`" class="btn-secondary">
              <i class="fas fa-cogs"></i>
              Configure Workflows
            </router-link>
            <router-link :to="`/projects?client=${client.id}`" class="btn-primary">
              <i class="fas fa-plus"></i>
              Create Project
            </router-link>
          </div>
        </div>

        <div class="project-filters">
          <button 
            v-for="phase in projectPhases" 
            :key="phase"
            :class="['filter-btn', { active: activeFilter === phase }]"
            @click="setActiveFilter(phase)"
          >
            {{ phase }} ({{ getProjectCountByPhase(phase) }})
          </button>
        </div>

        <div :class="['projects-container', projectViewMode]">
          <div 
            v-for="project in filteredProjects" 
            :key="project.id" 
            class="project-card"
            @click="navigateToProject(project.id)"
          >
            <div class="project-header">
              <h3>{{ project.name }}</h3>
              <div class="project-phase" :class="project.phase.toLowerCase()">
                {{ project.phase }}
              </div>
            </div>
            
            <div class="project-meta">
              <div class="meta-row">
                <span class="label">Started:</span>
                <span class="value">{{ project.startDate }}</span>
              </div>
              <div class="meta-row">
                <span class="label">Coverage:</span>
                <span class="value">{{ project.coverageTypes.join(', ') }}</span>
              </div>
              <div class="meta-row">
                <span class="label">Subjects:</span>
                <span class="value">{{ project.subjectCount }} active</span>
              </div>
            </div>

            <div class="project-progress">
              <div class="progress-bar">
                <div 
                  class="progress-fill" 
                  :style="{ width: project.completionPercentage + '%' }"
                  :class="project.phase.toLowerCase()"
                ></div>
              </div>
              <span class="progress-text">{{ project.completionPercentage }}% Complete</span>
            </div>

            <div class="project-stats">
              <div class="stat-item">
                <span class="stat-number">{{ project.gapsFound }}</span>
                <span class="stat-label">Gaps</span>
              </div>
              <div class="stat-item">
                <span class="stat-number">{{ project.resolved }}</span>
                <span class="stat-label">Resolved</span>
              </div>
              <div class="stat-item">
                <span class="stat-number">{{ project.pending }}</span>
                <span class="stat-label">Pending</span>
              </div>
            </div>

            <div class="project-actions">
              <router-link :to="`/projects/${project.id}`" class="btn-secondary" @click.stop>
                <i class="fas fa-folder-open"></i> Open Project
              </router-link>
              <router-link :to="`/projects/${project.id}/settings`" class="btn-secondary" @click.stop>
                <i class="fas fa-cogs"></i> Settings
              </router-link>
              <button class="btn-primary" @click.stop="continueProject(project.id)">
                Continue Work
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Cross-Project Analytics -->
      <div class="analytics-section">
        <h2>Cross-Project Analytics</h2>
        <div class="analytics-grid">
          <div class="analytics-card">
            <h3>Risk Distribution</h3>
            <div class="risk-breakdown">
              <div v-for="risk in client.riskBreakdown" :key="risk.level" class="risk-item">
                <div class="risk-bar" :class="risk.level.toLowerCase()">
                  <div class="risk-fill" :style="{ width: risk.percentage + '%' }"></div>
                </div>
                <div class="risk-label">
                  <span class="level">{{ risk.level }}</span>
                  <span class="count">{{ risk.count }} projects</span>
                </div>
              </div>
            </div>
          </div>

          <div class="analytics-card">
            <h3>Coverage Performance</h3>
            <div class="coverage-chart">
              <div v-for="coverage in client.coveragePerformance" :key="coverage.type" class="coverage-item">
                <div class="coverage-header">
                  <span class="coverage-name">{{ coverage.type }}</span>
                  <span class="coverage-score">{{ coverage.score }}%</span>
                </div>
                <div class="coverage-bar">
                  <div 
                    class="coverage-fill" 
                    :style="{ width: coverage.score + '%' }"
                    :class="getCoverageClass(coverage.score)"
                  ></div>
                </div>
              </div>
            </div>
          </div>

          <div class="analytics-card">
            <h3>Project Timeline</h3>
            <div class="timeline-chart">
              <div v-for="month in client.projectTimeline" :key="month.period" class="timeline-item">
                <div class="timeline-period">{{ month.period }}</div>
                <div class="timeline-bar">
                  <div 
                    class="timeline-segment discovery" 
                    :style="{ width: month.discovery + '%' }"
                  ></div>
                  <div 
                    class="timeline-segment analysis" 
                    :style="{ width: month.analysis + '%' }"
                  ></div>
                  <div 
                    class="timeline-segment active" 
                    :style="{ width: month.active + '%' }"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Client Activity Feed -->
      <div class="activity-section">
        <h2>Recent Client Activity</h2>
        <div class="activity-timeline">
          <div v-for="activity in client.recentActivity" :key="activity.id" class="activity-item">
            <div class="activity-icon" :class="activity.type">
              <i :class="activity.icon"></i>
            </div>
            <div class="activity-content">
              <div class="activity-header">
                <h4>{{ activity.title }}</h4>
                <span class="activity-time">{{ activity.timeAgo }}</span>
              </div>
              <p class="activity-description">{{ activity.description }}</p>
              <div class="activity-project">
                <span class="project-tag">{{ activity.projectName }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'ClientDetail',
  data() {
    return {
      loading: true,
      client: null,
      projectViewMode: 'grid',
      activeFilter: 'All',
      projectPhases: ['All', 'Discovery', 'Analysis', 'Active', 'Closed']
    };
  },
  computed: {
    filteredProjects() {
      if (this.activeFilter === 'All') {
        return this.client?.projects || [];
      }
      return this.client?.projects.filter(project => project.phase === this.activeFilter) || [];
    }
  },
  async created() {
    await this.loadClientData();
  },
  methods: {
    async loadClientData() {
      try {
        const clientId = this.$route.params.id;
        this.loading = true;

        // Load real client and projects data
        const [clientResponse, projectsResponse] = await Promise.all([
          api.get(`/clients/${clientId}/`),
          api.get(`/projects/?client=${clientId}`)
        ]);

        // Handle projects response format - extract the array from the wrapper object
        const projectsData = projectsResponse.data.projects || projectsResponse.data;

        this.client = {
          ...clientResponse.data,
          // Add calculated fields
          activeSubjects: projectsData.length * 15, // Mock: avg 15 subjects per project
          pendingDecisions: Math.floor(projectsData.length * 0.3), // Mock: 30% projects have pending
          monthlyTrend: Math.floor(Math.random() * 15) + 5, // Mock trend data
          projects: projectsData.map(project => ({
            id: project.id,
            name: project.name,
            phase: this.getProjectPhase(project),
            startDate: new Date(project.created_at).toLocaleDateString(),
            coverageTypes: this.getCoverageTypes(project.project_type),
            subjectCount: Math.floor(Math.random() * 30) + 10, // Mock subject count
            completionPercentage: this.calculateCompletionPercentage(project),
            gapsFound: Math.floor(Math.random() * 20) + 5, // Mock gaps
            resolved: Math.floor(Math.random() * 10) + 2, // Mock resolved
            pending: Math.floor(Math.random() * 10) + 1 // Mock pending
          })),
          // Add mock analytics data (TODO: replace with real analytics when available)
          riskBreakdown: this.generateRiskBreakdown(projectsData.length),
          coveragePerformance: this.generateCoveragePerformance(),
          projectTimeline: this.generateProjectTimeline(),
          recentActivity: this.generateRecentActivity(projectsData)
        };

        this.loading = false;
      } catch (error) {
        console.error('Error loading client data:', error);
        this.loading = false;
        // Fallback to mock data if API fails
        const routeClientId = this.$route.params.id;
        this.client = this.getMockClientData(routeClientId);
      }
    },

    getProjectPhase(project) {
      // Mock project phases based on creation date (TODO: implement real lifecycle tracking)
      const daysSinceCreation = Math.floor((new Date() - new Date(project.created_at)) / (1000 * 60 * 60 * 24));
      
      if (daysSinceCreation < 7) return 'Discovery';
      if (daysSinceCreation < 21) return 'Analysis';
      if (daysSinceCreation < 90) return 'Active';
      return 'Closed';
    },

    getCoverageTypes(projectType) {
      const coverageMap = {
        'RISK_REVIEW_INS': ['General Liability', 'Professional Liability'],
        'CERT_REVIEW': ['General Liability', 'Workers Comp'],
        'COMPLIANCE': ['General Liability', 'Umbrella'],
        'GENERAL': ['General Liability']
      };
      return coverageMap[projectType] || ['General Liability'];
    },

    calculateCompletionPercentage(project) {
      // Mock completion based on project age
      const daysSinceCreation = Math.floor((new Date() - new Date(project.created_at)) / (1000 * 60 * 60 * 24));
      return Math.min(Math.floor(daysSinceCreation * 2), 100);
    },

    generateRiskBreakdown(projectCount) {
      return [
        { level: 'High', count: Math.floor(projectCount * 0.2), percentage: 20 },
        { level: 'Medium', count: Math.floor(projectCount * 0.5), percentage: 50 },
        { level: 'Low', count: Math.floor(projectCount * 0.3), percentage: 30 }
      ];
    },

    generateCoveragePerformance() {
      return [
        { type: 'General Liability', score: 95 },
        { type: 'Professional Liability', score: 88 },
        { type: 'Workers Compensation', score: 92 },
        { type: 'Umbrella Coverage', score: 78 }
      ];
    },

    generateProjectTimeline() {
      return [
        { period: 'This Month', discovery: 30, analysis: 40, active: 30 },
        { period: 'Last Month', discovery: 20, analysis: 50, active: 30 },
        { period: '2 Months Ago', discovery: 40, analysis: 30, active: 30 }
      ];
    },

    generateRecentActivity(projects) {
      const activities = [];
      const recentProjects = projects.slice(0, 3);

      recentProjects.forEach((project, index) => {
        activities.push({
          id: index + 1,
          icon: 'fas fa-plus-circle',
          title: 'Project created',
          description: `${project.name} started in ${project.project_type_display || project.project_type} phase`,
          projectName: project.name,
          type: 'success',
          timeAgo: this.getTimeAgo(project.created_at)
        });
      });

      return activities;
    },

    getTimeAgo(dateString) {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
      
      if (diffInHours < 1) return 'Less than an hour ago';
      if (diffInHours < 24) return `${diffInHours} hours ago`;
      
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays === 1) return '1 day ago';
      if (diffInDays < 7) return `${diffInDays} days ago`;
      
      return date.toLocaleDateString();
    },
    
    getMockClientData(clientId) {
      const clients = {
        '1': {
          id: 1,
          name: 'ABC Construction LLC',
          type: 'General Contractor',
          status: 'active',
          complianceScore: 92,
          joinDate: 'January 2023',
          primaryContact: 'John Smith',
          email: 'john.smith@abcconstruction.com',
          activeSubjects: 120,
          pendingDecisions: 5,
          monthlyTrend: 8,
          projects: [
            {
              id: 101,
              name: 'Downtown Office Complex',
              phase: 'Active',
              startDate: '2024-01-15',
              coverageTypes: ['General Liability', 'Professional Liability'],
              subjectCount: 45,
              completionPercentage: 75,
              gapsFound: 12,
              resolved: 8,
              pending: 4
            },
            {
              id: 102,
              name: 'Metro Shopping Center',
              phase: 'Analysis',
              startDate: '2024-03-01',
              coverageTypes: ['General Liability', 'Workers Comp'],
              subjectCount: 32,
              completionPercentage: 45,
              gapsFound: 8,
              resolved: 3,
              pending: 5
            },
            {
              id: 103,
              name: 'Residential Tower Project',
              phase: 'Discovery',
              startDate: '2024-07-10',
              coverageTypes: ['General Liability', 'Umbrella'],
              subjectCount: 18,
              completionPercentage: 25,
              gapsFound: 15,
              resolved: 2,
              pending: 13
            }
          ],
          riskBreakdown: [
            { level: 'High', count: 5, percentage: 33 },
            { level: 'Medium', count: 7, percentage: 47 },
            { level: 'Low', count: 3, percentage: 20 }
          ],
          coveragePerformance: [
            { type: 'General Liability', score: 95 },
            { type: 'Professional Liability', score: 88 },
            { type: 'Workers Compensation', score: 92 },
            { type: 'Umbrella Coverage', score: 78 }
          ],
          projectTimeline: [
            { period: 'Jul 2024', discovery: 60, analysis: 30, active: 10 },
            { period: 'Aug 2024', discovery: 40, analysis: 35, active: 25 },
            { period: 'Sep 2024', discovery: 20, analysis: 40, active: 40 }
          ],
          recentActivity: [
            {
              id: 1,
              icon: 'fas fa-check-circle',
              title: 'Gap resolved',
              description: 'Professional liability coverage increased to meet requirements',
              projectName: 'Downtown Office Complex',
              type: 'success',
              timeAgo: '2 hours ago'
            },
            {
              id: 2,
              icon: 'fas fa-exclamation-triangle',
              title: 'New gap identified',
              description: 'Additional insured requirements missing for subcontractors',
              projectName: 'Metro Shopping Center',
              type: 'warning',
              timeAgo: '1 day ago'
            },
            {
              id: 3,
              icon: 'fas fa-plus-circle',
              title: 'New subjects added',
              description: '5 new subcontractors onboarded for tower project',
              projectName: 'Residential Tower Project',
              type: 'info',
              timeAgo: '3 days ago'
            }
          ]
        }
        // Add more mock clients as needed
      };
      
      return clients[clientId] || null;
    },
    
    simulateApiCall() {
      return new Promise(resolve => setTimeout(resolve, 800));
    },
    
    toggleProjectView() {
      this.projectViewMode = this.projectViewMode === 'grid' ? 'list' : 'grid';
    },
    
    setActiveFilter(phase) {
      this.activeFilter = phase;
    },
    
    getProjectCountByPhase(phase) {
      if (phase === 'All') return this.client?.projects.length || 0;
      return this.client?.projects.filter(p => p.phase === phase).length || 0;
    },
    
    getScoreClass(score) {
      if (score >= 90) return 'excellent';
      if (score >= 80) return 'good';
      if (score >= 70) return 'fair';
      return 'poor';
    },
    
    getCoverageClass(score) {
      if (score >= 90) return 'excellent';
      if (score >= 80) return 'good';
      if (score >= 70) return 'fair';
      return 'poor';
    },
    
    navigateToProject(projectId) {
      this.$router.push(`/projects/${projectId}`);
    },
    
    viewProjectDetails(projectId) {
      this.$router.push(`/projects/${projectId}/details`);
    },
    
    continueProject(projectId) {
      this.$router.push(`/projects/${projectId}/workflow`);
    }
  }
};
</script>

<style scoped>
/* Client Detail Styles */
.client-detail {
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
}

.loading-state, .error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #718096;
}

.loading-state i {
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #667eea;
}

.error-state i {
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #ef4444;
}

/* Client Header */
.client-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 3rem;
  color: white;
  margin-bottom: 2rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2rem;
}

.breadcrumb {
  font-size: 0.9rem;
  margin-bottom: 1rem;
  opacity: 0.9;
}

.breadcrumb a {
  color: white;
  text-decoration: none;
}

.breadcrumb a:hover {
  text-decoration: underline;
}

.separator {
  margin: 0 0.5rem;
}

.current {
  opacity: 0.7;
}

.client-name {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
}

.client-type {
  font-size: 1.2rem;
  opacity: 0.9;
  margin-bottom: 1.5rem;
}

.client-meta {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  opacity: 0.95;
}

.client-status-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 2rem;
  border-radius: 12px;
  backdrop-filter: blur(10px);
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.2);
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: currentColor;
}

.compliance-score {
  text-align: center;
}

.score-circle {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 4px solid rgba(255, 255, 255, 0.3);
  position: relative;
}

.score-circle.excellent { border-color: #10b981; }
.score-circle.good { border-color: #3b82f6; }
.score-circle.fair { border-color: #f59e0b; }
.score-circle.poor { border-color: #ef4444; }

.score-number {
  font-size: 1.8rem;
  font-weight: 700;
}

.score-label {
  font-size: 0.9rem;
  opacity: 0.8;
}

/* Metrics Section */
.metrics-section {
  margin-bottom: 3rem;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.metric-card {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.metric-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
}

.metric-content h3 {
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  color: #2d3748;
}

.metric-content p {
  margin: 0;
  color: #718096;
  font-size: 0.9rem;
}

/* Projects Section */
.projects-section {
  margin-bottom: 3rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.section-header h2 {
  font-size: 1.8rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.section-actions {
  display: flex;
  gap: 1rem;
}

.project-filters {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.filter-btn {
  padding: 0.75rem 1.5rem;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 8px;
  font-weight: 500;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-btn:hover {
  background: #f7fafc;
  border-color: #cbd5e0;
}

.filter-btn.active {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border-color: transparent;
}

.projects-container {
  display: grid;
  gap: 1.5rem;
}

.projects-container.grid {
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
}

.projects-container.list {
  grid-template-columns: 1fr;
}

.project-card {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.3s ease;
}

.project-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.project-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.project-header h3 {
  font-size: 1.3rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.project-phase {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  color: white;
}

.project-phase.discovery { background: linear-gradient(135deg, #48bb78, #38a169); }
.project-phase.analysis { background: linear-gradient(135deg, #667eea, #764ba2); }
.project-phase.active { background: linear-gradient(135deg, #ed8936, #dd6b20); }
.project-phase.closed { background: linear-gradient(135deg, #4299e1, #3182ce); }

.project-meta {
  margin-bottom: 1.5rem;
}

.meta-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.meta-row .label {
  color: #718096;
  font-weight: 500;
}

.meta-row .value {
  color: #2d3748;
  font-weight: 600;
}

.project-progress {
  margin-bottom: 1.5rem;
}

.progress-bar {
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.progress-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-fill.discovery { background: linear-gradient(135deg, #48bb78, #38a169); }
.progress-fill.analysis { background: linear-gradient(135deg, #667eea, #764ba2); }
.progress-fill.active { background: linear-gradient(135deg, #ed8936, #dd6b20); }
.progress-fill.closed { background: linear-gradient(135deg, #4299e1, #3182ce); }

.progress-text {
  font-size: 0.85rem;
  color: #718096;
}

.project-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
  text-align: center;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-number {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
}

.stat-label {
  font-size: 0.8rem;
  color: #718096;
}

.project-actions {
  display: flex;
  gap: 1rem;
}

.btn-secondary, .btn-primary {
  flex: 1;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  text-align: center;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
}

.btn-secondary {
  background: #edf2f7;
  color: #4a5568;
}

.btn-secondary:hover {
  background: #e2e8f0;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #5a67d8, #6b46c1);
}

/* Analytics Section */
.analytics-section {
  margin-bottom: 3rem;
}

.analytics-section h2 {
  font-size: 1.8rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 2rem;
}

.analytics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
}

.analytics-card {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
}

.analytics-card h3 {
  font-size: 1.3rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 1.5rem;
}

.risk-breakdown {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.risk-item {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.risk-bar {
  flex: 1;
  height: 12px;
  border-radius: 6px;
  overflow: hidden;
  position: relative;
}

.risk-bar.high { background: #fecaca; }
.risk-bar.medium { background: #fed7aa; }
.risk-bar.low { background: #bbf7d0; }

.risk-fill {
  height: 100%;
  border-radius: 6px;
  transition: width 0.3s ease;
}

.risk-bar.high .risk-fill { background: #ef4444; }
.risk-bar.medium .risk-fill { background: #f59e0b; }
.risk-bar.low .risk-fill { background: #10b981; }

.risk-label {
  min-width: 120px;
  text-align: right;
}

.risk-label .level {
  font-weight: 600;
  color: #2d3748;
}

.risk-label .count {
  font-size: 0.85rem;
  color: #718096;
  margin-left: 0.5rem;
}

.coverage-chart {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.coverage-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.coverage-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.coverage-name {
  font-weight: 600;
  color: #2d3748;
}

.coverage-score {
  font-weight: 700;
  color: #667eea;
}

.coverage-bar {
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
}

.coverage-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.coverage-fill.excellent { background: #10b981; }
.coverage-fill.good { background: #3b82f6; }
.coverage-fill.fair { background: #f59e0b; }
.coverage-fill.poor { background: #ef4444; }

.timeline-chart {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.timeline-item {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.timeline-period {
  min-width: 80px;
  font-weight: 600;
  color: #2d3748;
  font-size: 0.9rem;
}

.timeline-bar {
  flex: 1;
  height: 20px;
  border-radius: 10px;
  background: #f7fafc;
  display: flex;
  overflow: hidden;
}

.timeline-segment {
  height: 100%;
  transition: width 0.3s ease;
}

.timeline-segment.discovery { background: linear-gradient(135deg, #48bb78, #38a169); }
.timeline-segment.analysis { background: linear-gradient(135deg, #667eea, #764ba2); }
.timeline-segment.active { background: linear-gradient(135deg, #ed8936, #dd6b20); }

/* Activity Section */
.activity-section h2 {
  font-size: 1.8rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 2rem;
}

.activity-timeline {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  max-height: 500px;
  overflow-y: auto;
}

.activity-item {
  display: flex;
  gap: 1.5rem;
  padding: 2rem;
  border-bottom: 1px solid #e2e8f0;
}

.activity-item:last-child {
  border-bottom: none;
}

.activity-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.activity-icon.success { background: linear-gradient(135deg, #10b981, #065f46); }
.activity-icon.warning { background: linear-gradient(135deg, #f59e0b, #92400e); }
.activity-icon.info { background: linear-gradient(135deg, #3b82f6, #1e40af); }

.activity-content {
  flex: 1;
}

.activity-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.activity-header h4 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.activity-time {
  font-size: 0.85rem;
  color: #718096;
}

.activity-description {
  color: #4a5568;
  line-height: 1.5;
  margin-bottom: 1rem;
}

.project-tag {
  display: inline-block;
  background: #edf2f7;
  color: #667eea;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
}

/* Responsive Design */
@media (max-width: 768px) {
  .client-detail {
    padding: 1rem;
  }
  
  .header-content {
    flex-direction: column;
    gap: 2rem;
  }
  
  .client-header {
    padding: 2rem;
  }
  
  .metrics-grid {
    grid-template-columns: 1fr;
  }
  
  .analytics-grid {
    grid-template-columns: 1fr;
  }
  
  .projects-container.grid {
    grid-template-columns: 1fr;
  }
  
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .project-actions {
    flex-direction: column;
  }
}
</style> 